import React from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Drawer, IconButton, InputAdornment, MenuItem,
  Stack, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import PanToolAltIcon from "@mui/icons-material/PanToolAlt";
import PercentIcon from "@mui/icons-material/Percent";
import BoltIcon from "@mui/icons-material/Bolt";
import SpeedIcon from "@mui/icons-material/Speed";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import SearchIcon from "@mui/icons-material/Search";
import {
  CATEGORY_LABEL, groupedObjectives, ObjectiveCategory, WorkflowObjective,
} from "./workflowObjectives";
import {
  useListWorkflowObjectivesQuery,
  useCreateWorkflowObjectiveMutation,
} from "../../../../Api/attoDeskApi";

/**
 * T63 Part J + O — objective-first workflow creation.
 *
 * Add Workflow opens this component as a RIGHT-SIDE Drawer (Part O,
 * previously a centred Dialog). The workflows list stays visible
 * behind the panel so operators can see the existing catalogue while
 * choosing. Only after selection does the router push to
 * /workflows/new?objective=<key> and the editor initialises the
 * standard Probe/Acquisition/Mastered/Waiting structure via
 * createStandardWorkflowForObjective.
 *
 * Not-yet-implemented objective families (Frequency / Rate / Duration
 * / Manual) are rendered in the list but disabled with a
 * "Not available yet" chip — they cannot navigate into the editor
 * because the phase criteria UI still assumes Percent Correct and
 * would confuse the operator. Percent Correct family remains the
 * only fully-selectable set.
 */

const CATEGORY_ICON: Record<ObjectiveCategory, React.ComponentType<any>> = {
  percent_correct: PercentIcon,
  frequency:       BoltIcon,
  rate:            SpeedIcon,
  duration:        AccessTimeIcon,
  manual:          PanToolAltIcon,
};

export interface ObjectiveChooserDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (objective: WorkflowObjective) => void;
}

export default function ObjectiveChooserDialog({
  open, onClose, onSelect,
}: ObjectiveChooserDialogProps) {
  const [search, setSearch] = React.useState("");
  const [createOpen, setCreateOpen] = React.useState(false);
  const builtInGroups = React.useMemo(() => groupedObjectives(), []);

  // T63 Part O — DB-backed custom objectives. Fetch on open; merge into
  // the built-in groups so search + selection work over the union.
  const { data: customResp } = useListWorkflowObjectivesQuery(undefined, { skip: !open });
  const customRows: any[] = Array.isArray(customResp?.data) ? (customResp!.data as any[]) : [];

  const allGroups = React.useMemo(() => {
    const groups = builtInGroups.map((g) => ({ ...g, items: [...g.items] }));
    // Custom objectives (isSystem=0) rendered under their own "CUSTOM" bucket.
    const customItems: WorkflowObjective[] = customRows
      .filter((r) => Number(r.isSystem) === 0 && Number(r.isActive) !== 0)
      .map((r) => ({
        key: String(r.objectiveKey),
        label: String(r.name),
        category: (["percent_correct","frequency","rate","duration","manual"] as const)
          .includes(r.category) ? r.category as ObjectiveCategory : "manual",
        direction: String(r.direction) as WorkflowObjective["direction"],
        fullyImplemented: true,
      }));
    if (customItems.length) {
      groups.push({ category: "manual", items: customItems, _isCustom: true } as any);
    }
    return groups;
  }, [builtInGroups, customRows]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allGroups;
    return allGroups
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (o) => o.label.toLowerCase().includes(q) || String(g.category).includes(q)
              || CATEGORY_LABEL[g.category].toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [allGroups, search]);

  // Reset search when dialog re-opens so a stale query doesn't hide
  // the list on next Add Workflow click.
  React.useEffect(() => { if (open) setSearch(""); }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "min(480px, calc(100vw - 32px))" },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
        },
      }}
      // Keeps the underlying Workflows list visible; the operator can
      // see the existing catalogue while picking a new objective.
      ModalProps={{ keepMounted: false }}
    >
      {/* Sticky panel header */}
      <Box
        sx={{
          px: 3, py: 2.25,
          borderBottom: "1px solid #E3EBF2",
          flexShrink: 0,
        }}
      >
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: "#274F70" }}>
              Choose Workflow Objective
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#64788B", mt: 0.5 }}>
              Select what this workflow is designed to improve or reduce.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{ color: "#64788B", ml: 1, mt: -0.5 }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <TextField
          fullWidth
          size="small"
          placeholder="Search objectives"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "#94A3B8" }} />
              </InputAdornment>
            ),
          }}
          sx={{ mt: 2 }}
        />
        {/* T63 Part O — Create custom objective affordance. Placed
            directly under the search field so it is visible immediately
            when the drawer opens, not buried at the bottom. */}
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => setCreateOpen(true)}
          sx={{
            mt: 1.5,
            textTransform: "none",
            fontWeight: 600,
            borderColor: "#BFDBFE",
            color: "#1D4ED8",
            "&:hover": { borderColor: "#1D4ED8", backgroundColor: "#EAF2FF" },
          }}
        >
          Create New Objective
        </Button>
      </Box>
      {/* Scrollable objective list */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
        <Stack spacing={2}>
          {filtered.length === 0 && (
            <Typography sx={{ fontSize: 13, color: "#64788B", py: 3, textAlign: "center" }}>
              No objectives match “{search}”.
            </Typography>
          )}
          {filtered.map((g: any, idx: number) => {
            const isCustom = Boolean(g._isCustom);
            const CatIcon = CATEGORY_ICON[g.category as ObjectiveCategory];
            const heading = isCustom ? "Custom Objectives" : CATEGORY_LABEL[g.category as ObjectiveCategory];
            return (
              <Box key={`${g.category}-${idx}${isCustom ? "-custom" : ""}`}>
                <Typography
                  sx={{
                    fontSize: 10, fontWeight: 800, letterSpacing: "0.08em",
                    textTransform: "uppercase", color: "#61758A", mb: 0.75,
                    display: "flex", alignItems: "center", gap: 0.75,
                  }}
                >
                  <CatIcon fontSize="small" sx={{ fontSize: 14, color: "#7EA3C3" }} />
                  {heading}
                </Typography>
                <Stack spacing={0.5}>
                  {g.items.map((o: WorkflowObjective) => (
                    <ObjectiveRow key={o.key} objective={o} onSelect={onSelect} />
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </Box>
      <CreateObjectiveDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => setCreateOpen(false)}
      />
    </Drawer>
  );
}

/** T63 Part O — dialog for creating a new custom objective. Fields are
 *  strictly server-validated; frontend only offers well-formed choices. */
const AGG_BY_CATEGORY: Record<string, { value: string; label: string }[]> = {
  percent_correct: [{ value: "percent_correct", label: "Percent Correct" }],
  frequency:       [{ value: "frequency_count", label: "Frequency Count" }],
  rate:            [
    { value: "rate_per_minute", label: "Rate per Minute" },
    { value: "rate_per_hour",   label: "Rate per Hour" },
  ],
  duration:        [
    { value: "duration_total", label: "Total Duration" },
    { value: "duration_avg",   label: "Average Duration" },
    { value: "duration_min",   label: "Minimum Duration" },
    { value: "duration_max",   label: "Maximum Duration" },
  ],
  manual:          [{ value: "manual_only", label: "Manual (no threshold)" }],
};
const UNIT_HINT: Record<string, string> = {
  percent_correct: "%",
  frequency_count: "occurrences",
  rate_per_minute: "per minute",
  rate_per_hour:   "per hour",
  duration_total:  "seconds",
  duration_avg:    "seconds",
  duration_min:    "seconds",
  duration_max:    "seconds",
  manual_only:     "—",
};

function CreateObjectiveDialog({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const [name, setName] = React.useState("");
  const [category, setCategory] = React.useState("frequency");
  const [direction, setDirection] = React.useState("increase");
  const [agg, setAgg] = React.useState("frequency_count");
  const [description, setDescription] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [create, { isLoading }] = useCreateWorkflowObjectiveMutation();

  // When family changes, reset aggregation to that family's first option
  // and, if the family is manual, force direction to manual.
  React.useEffect(() => {
    const options = AGG_BY_CATEGORY[category] ?? [];
    if (options.length) setAgg(options[0].value);
    if (category === "manual") setDirection("manual");
    else if (direction === "manual") setDirection("increase");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  React.useEffect(() => {
    if (open) {
      setName(""); setCategory("frequency"); setDirection("increase");
      setAgg("frequency_count"); setDescription(""); setError(null);
    }
  }, [open]);

  const submit = async () => {
    setError(null);
    if (!name.trim()) { setError("Objective name is required."); return; }
    try {
      const res: any = await create({
        name: name.trim(), category, direction, aggregationType: agg,
        description: description.trim() || undefined,
      }).unwrap();
      if (!res?.status) { setError(res?.message || "Save failed."); return; }
      onCreated();
    } catch (e: any) {
      setError(e?.data?.message || "Save failed.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: "#274F70" }}>
          Create Workflow Objective
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: "#64788B", mt: 0.5 }}>
          Define how this objective should measure target progress.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            fullWidth size="small" label="Objective name" required
            value={name} onChange={(e) => setName(e.target.value)}
            inputProps={{ maxLength: 191, "aria-label": "Objective name" }}
          />
          <TextField
            select fullWidth size="small" label="Objective family"
            value={category} onChange={(e) => setCategory(e.target.value)}
          >
            <MenuItem value="percent_correct">Percent Correct</MenuItem>
            <MenuItem value="frequency">Frequency</MenuItem>
            <MenuItem value="rate">Rate</MenuItem>
            <MenuItem value="duration">Duration</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
          </TextField>
          <TextField
            select fullWidth size="small" label="Direction"
            value={direction} onChange={(e) => setDirection(e.target.value)}
            disabled={category === "manual"}
          >
            <MenuItem value="increase">Increase (mastery when ≥ threshold)</MenuItem>
            <MenuItem value="decrease">Reduce (mastery when ≤ threshold)</MenuItem>
            <MenuItem value="manual">Manual (no automatic evaluation)</MenuItem>
          </TextField>
          <TextField
            select fullWidth size="small" label="Measurement"
            value={agg} onChange={(e) => setAgg(e.target.value)}
          >
            {(AGG_BY_CATEGORY[category] ?? []).map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth size="small" label="Unit"
            value={UNIT_HINT[agg] ?? ""}
            InputProps={{ readOnly: true }}
            helperText="Derived from measurement — not editable."
          />
          <TextField
            fullWidth size="small" label="Description (optional)" multiline minRows={2}
            value={description} onChange={(e) => setDescription(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
          {error && (
            <Typography sx={{ fontSize: 12, color: "#DC2626" }}>{error}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained" onClick={submit} disabled={isLoading}
          sx={{ textTransform: "none", backgroundColor: "#1D4ED8", "&:hover": { backgroundColor: "#14509E" } }}
        >
          Create Objective
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ObjectiveRow({
  objective, onSelect,
}: { objective: WorkflowObjective; onSelect: (o: WorkflowObjective) => void }) {
  const DirIcon =
    objective.direction === "increase" ? ArrowUpwardIcon :
    objective.direction === "decrease" ? ArrowDownwardIcon :
    PanToolAltIcon;
  const dirColor =
    objective.direction === "increase" ? "#22C55E" :
    objective.direction === "decrease" ? "#F97316" :
    "#7A8C9D";
  // T63 Part O — rows for objectives that aren't fully implemented
  // become non-interactive with a "Not available yet" chip. Prevents
  // creating a workflow whose phase criteria UI would still assume
  // Percent Correct (a real functional contradiction until the
  // family-aware evaluator ships).
  const disabled = !objective.fullyImplemented;
  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onClick={disabled ? undefined : () => onSelect(objective)}
      onKeyDown={disabled ? undefined : (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(objective); }
      }}
      aria-label={objective.label}
      sx={{
        display: "flex", alignItems: "center", gap: 1.25,
        px: 1.5, py: 1, borderRadius: "10px",
        border: "1px solid #DCE5ED",
        bgcolor: disabled ? "#F8FAFC" : "#FFFFFF",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.72 : 1,
        transition: "all 150ms ease",
        ...(disabled ? {} : {
          "&:hover": { bgcolor: "#F5F8FB", borderColor: "#7EA3C3" },
          "&:focus-visible": {
            outline: "none",
            boxShadow: "0 0 0 3px rgba(59,130,246,0.22)",
            borderColor: "#3B82F6",
          },
        }),
      }}
    >
      <DirIcon fontSize="small" sx={{ fontSize: 16, color: disabled ? "#94A3B8" : dirColor }} />
      <Typography sx={{
        flex: 1, fontSize: 13.5, fontWeight: 600,
        color: disabled ? "#64788B" : "#274F70",
      }}>
        {objective.label}
      </Typography>
      {disabled && (
        <Chip
          size="small"
          label={objective.disabledReason ?? "Not available yet"}
          sx={{
            height: 20, fontSize: 10, fontWeight: 700,
            bgcolor: "#F1F5F9", color: "#64748B",
            border: "1px solid #DCE5ED",
          }}
        />
      )}
    </Box>
  );
}
